import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, RotateCcw, Clock, CheckCircle, XCircle, Gauge, AlertTriangle, Monitor, Smartphone } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';

const GAME_STATES = {
  READY: 'ready',
  DEVICE_SELECT: 'device_select',
  PLAYING: 'playing',
  FINISHED: 'finished',
};

// Device Mode Selection Component
const DeviceModeSelector = ({ onSelect }) => (
  <Card className="border-slate-200">
    <CardContent className="pt-8">
      <div className="text-center mb-6">
        <h2 className="font-heading text-xl font-bold text-[#0B1D51] mb-2">
          Select Your Device
        </h2>
        <p className="text-sm text-[#64748B]">
          Choose how you're taking this test for the best experience
        </p>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <Button
          variant="outline"
          className="h-32 flex-col gap-3 hover:border-[#0B1D51] hover:bg-[#0B1D51]/5"
          onClick={() => onSelect('desktop')}
          data-testid="select-desktop"
        >
          <Monitor className="w-10 h-10 text-[#0B1D51]" />
          <div>
            <p className="font-semibold">Laptop/Desktop</p>
            <p className="text-xs text-[#64748B]">Keyboard controls</p>
          </div>
        </Button>
        
        <Button
          variant="outline"
          className="h-32 flex-col gap-3 hover:border-[#FF9F1C] hover:bg-[#FF9F1C]/5"
          onClick={() => onSelect('mobile')}
          data-testid="select-mobile"
        >
          <Smartphone className="w-10 h-10 text-[#FF9F1C]" />
          <div>
            <p className="font-semibold">Mobile/Tablet</p>
            <p className="text-xs text-[#64748B]">Touch controls</p>
          </div>
        </Button>
      </div>
    </CardContent>
  </Card>
);

// SVG Dial Component - Using polygon paths for guaranteed accuracy
const Dial = ({ value, label, position }) => {
  // Zone determination
  const getZone = (v) => {
    if (v <= 30) return 'red';
    if (v <= 50) return 'orange';
    return 'green';
  };
  
  const zone = getZone(value);
  const zoneColor = zone === 'red' ? '#DC2626' : zone === 'orange' ? '#EA580C' : '#16A34A';
  
  // Calculate SVG point for a given value (0-100) at specified radius
  const valueToPoint = (v, r) => {
    const angle = -135 + (v / 100) * 270;
    const rad = angle * Math.PI / 180;
    return {
      x: 60 + r * Math.sin(rad),
      y: 60 - r * Math.cos(rad)
    };
  };
  
  // Create zone path using many small line segments (guaranteed accuracy)
  const createZonePath = (startVal, endVal, radius) => {
    const points = ['60,60']; // Start at center
    const steps = 40;
    for (let i = 0; i <= steps; i++) {
      const v = startVal + (endVal - startVal) * (i / steps);
      const p = valueToPoint(v, radius);
      points.push(`${p.x.toFixed(1)},${p.y.toFixed(1)}`);
    }
    return points.join(' ');
  };
  
  // Needle tip position
  const needleTip = valueToPoint(value, 42);
  
  return (
    <div className="flex flex-col items-center">
      <svg viewBox="0 0 120 120" className="w-36 h-36 sm:w-44 sm:h-44">
        {/* Background circle */}
        <circle cx="60" cy="60" r="50" fill="#e5e7eb" stroke="#1f2937" strokeWidth="2"/>
        
        {/* Zone pie wedges using polygons - guaranteed to match needle positions */}
        <polygon points={createZonePath(0, 30, 48)} fill="#DC2626"/>
        <polygon points={createZonePath(30, 50, 48)} fill="#EA580C"/>
        <polygon points={createZonePath(50, 100, 48)} fill="#16A34A"/>
        
        {/* Inner circle for clean center */}
        <circle cx="60" cy="60" r="25" fill="white" stroke="#1f2937" strokeWidth="1"/>
        
        {/* Zone boundary lines at 0, 30, 50, 100 */}
        {[0, 30, 50, 100].map(v => {
          const p = valueToPoint(v, 48);
          return <line key={v} x1="60" y1="60" x2={p.x} y2={p.y} stroke="#1f2937" strokeWidth="2"/>;
        })}
        
        {/* Value markers outside the dial */}
        {[0, 30, 50, 100].map(v => {
          const p = valueToPoint(v, 56);
          return (
            <text key={v} x={p.x} y={p.y} fontSize="9" fill="#1f2937" fontWeight="bold" textAnchor="middle" dominantBaseline="middle">
              {v}
            </text>
          );
        })}
        
        {/* Zone labels */}
        <text x={valueToPoint(15, 38).x} y={valueToPoint(15, 38).y} fontSize="8" fill="white" fontWeight="bold" textAnchor="middle">HIGH</text>
        <text x={valueToPoint(40, 38).x} y={valueToPoint(40, 38).y} fontSize="8" fill="white" fontWeight="bold" textAnchor="middle">MED</text>
        <text x={valueToPoint(75, 38).x} y={valueToPoint(75, 38).y} fontSize="8" fill="white" fontWeight="bold" textAnchor="middle">LOW</text>
        
        {/* Center dot */}
        <circle cx="60" cy="60" r="6" fill="#1f2937"/>
        
        {/* Needle */}
        <line x1="60" y1="60" x2={needleTip.x} y2={needleTip.y} stroke="#1f2937" strokeWidth="4" strokeLinecap="round"/>
        
        {/* Colored dot at needle tip showing the zone */}
        <circle cx={needleTip.x} cy={needleTip.y} r="7" fill={zoneColor} stroke="white" strokeWidth="2"/>
      </svg>
      
      <div className="mt-1 px-3 py-1.5 bg-white border-2 border-black rounded text-sm font-semibold">
        {label}
      </div>
      <div className="mt-1 text-xl font-bold text-[#0B1D51]">{position}</div>
    </div>
  );
};

// Priority Legend
const PriorityLegend = () => (
  <div className="flex justify-center gap-4 sm:gap-6 mb-4">
    <div className="flex items-center gap-2">
      <div className="w-4 h-4 bg-[#EF4444] border border-black"></div>
      <span className="text-xs sm:text-sm">High</span>
    </div>
    <div className="flex items-center gap-2">
      <div className="w-4 h-4 bg-[#F97316] border border-black"></div>
      <span className="text-xs sm:text-sm">Medium</span>
    </div>
    <div className="flex items-center gap-2">
      <div className="w-4 h-4 bg-[#F8FAFC] border border-black"></div>
      <span className="text-xs sm:text-sm">Low</span>
    </div>
  </div>
);

// Generate a random dial configuration
const generateDialSet = () => {
  // Generate random values for each dial
  const brakePressure = Math.floor(Math.random() * 100);
  const batteryVoltage = Math.floor(Math.random() * 100);
  const fuel = Math.floor(Math.random() * 100);
  
  return {
    L: { value: brakePressure, label: 'Brake Pressure', position: 'L' },
    M: { value: batteryVoltage, label: 'Battery Voltage', position: 'M' },
    R: { value: fuel, label: 'Fuel', position: 'R' },
  };
};

// Calculate the correct viewing order based on rules
const calculateCorrectOrder = (dialSet) => {
  const getZone = (v) => {
    if (v <= 30) return 0; // Red - highest priority
    if (v <= 50) return 1; // Orange - medium priority
    return 2; // White - lowest priority
  };
  
  // System importance: Brake (L) > Battery (M) > Fuel (R)
  const systemPriority = { L: 0, M: 1, R: 2 };
  
  // Create sortable array
  const dials = [
    { pos: 'L', zone: getZone(dialSet.L.value), sys: systemPriority.L },
    { pos: 'M', zone: getZone(dialSet.M.value), sys: systemPriority.M },
    { pos: 'R', zone: getZone(dialSet.R.value), sys: systemPriority.R },
  ];
  
  // Sort by: 1) Zone priority (lower = higher priority), 2) System importance (lower = higher priority)
  dials.sort((a, b) => {
    if (a.zone !== b.zone) return a.zone - b.zone;
    return a.sys - b.sys;
  });
  
  return dials.map(d => d.pos).join('');
};

// Generate answer options (correct + 3 distractors)
const generateOptions = (correctOrder) => {
  const allPermutations = ['LMR', 'LRM', 'MLR', 'MRL', 'RLM', 'RML'];
  const distractors = allPermutations.filter(p => p !== correctOrder);
  
  // Shuffle and pick 3 distractors
  const shuffledDistractors = distractors.sort(() => Math.random() - 0.5).slice(0, 3);
  
  // Combine with correct answer and shuffle
  const options = [correctOrder, ...shuffledDistractors].sort(() => Math.random() - 0.5);
  
  return options;
};

// Timer Component
const GameTimer = ({ timeLeft, maxTime }) => {
  if (!maxTime) return null;
  
  const percentage = (timeLeft / maxTime) * 100;
  const isWarning = percentage < 25;
  
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-[#64748B]">Time Remaining</span>
        <span className={`font-mono font-medium ${isWarning ? 'text-red-500' : 'text-[#0B1D51]'}`}>
          {Math.ceil(timeLeft)}s
        </span>
      </div>
      <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
        <motion.div 
          className={`h-full ${isWarning ? 'bg-red-500' : 'bg-[#FF9F1C]'}`}
          initial={{ width: '100%' }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.1 }}
        />
      </div>
    </div>
  );
};

// Main Dials Game Component
const DialsGame = ({ level, onComplete }) => {
  const [gameState, setGameState] = useState(GAME_STATES.READY);
  const [deviceMode, setDeviceMode] = useState(null);
  const [currentRound, setCurrentRound] = useState(0);
  const [score, setScore] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [wrong, setWrong] = useState(0);
  const [dialSet, setDialSet] = useState(null);
  const [options, setOptions] = useState([]);
  const [correctOrder, setCorrectOrder] = useState('');
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const startTime = useRef(Date.now());
  const roundTimer = useRef(null);
  
  const totalRounds = 10;
  
  // Time limits per level (in seconds)
  const getTimeLimit = useCallback(() => {
    switch (level) {
      case 1: return null; // No time limit
      case 2: return 30;
      case 3: return 20;
      case 4: return 10;
      case 5: return 5;
      default: return null;
    }
  }, [level]);
  
  // Generate new round
  const generateNewRound = useCallback(() => {
    const newDialSet = generateDialSet();
    const correct = calculateCorrectOrder(newDialSet);
    const opts = generateOptions(correct);
    
    setDialSet(newDialSet);
    setCorrectOrder(correct);
    setOptions(opts);
    setSelectedAnswer(null);
    setFeedback(null);
    
    const limit = getTimeLimit();
    setTimeLeft(limit);
  }, [getTimeLimit]);
  
  // Start game
  const startGame = useCallback(() => {
    setGameState(GAME_STATES.PLAYING);
    setCurrentRound(0);
    setScore(0);
    setCorrect(0);
    setWrong(0);
    startTime.current = Date.now();
    generateNewRound();
  }, [generateNewRound]);
  
  // Timer effect
  useEffect(() => {
    if (gameState !== GAME_STATES.PLAYING || timeLeft === null || feedback) return;
    
    if (timeLeft <= 0) {
      // Time's up - count as wrong
      handleTimeout();
      return;
    }
    
    roundTimer.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 0.1) {
          clearInterval(roundTimer.current);
          return 0;
        }
        return prev - 0.1;
      });
    }, 100);
    
    return () => {
      if (roundTimer.current) clearInterval(roundTimer.current);
    };
  }, [gameState, timeLeft, feedback]);
  
  // Handle timeout
  const handleTimeout = () => {
    if (roundTimer.current) clearInterval(roundTimer.current);
    
    setWrong(w => w + 1);
    setFeedback({ type: 'timeout', message: `Time's up! Correct: ${correctOrder}` });
    
    setTimeout(() => {
      if (currentRound + 1 >= totalRounds) {
        finishGame();
      } else {
        setCurrentRound(r => r + 1);
        generateNewRound();
      }
    }, 2000);
  };
  
  // Handle answer selection
  const handleAnswer = (answer) => {
    if (feedback || selectedAnswer) return;
    
    if (roundTimer.current) clearInterval(roundTimer.current);
    
    setSelectedAnswer(answer);
    
    const isCorrect = answer === correctOrder;
    // Compute final tallies explicitly to avoid stale-closure on the last round
    const newScore = score + (isCorrect ? 10 : 0);
    const newCorrect = correct + (isCorrect ? 1 : 0);
    
    if (isCorrect) {
      setScore(newScore);
      setCorrect(newCorrect);
      setFeedback({ type: 'correct', message: 'Correct!' });
    } else {
      setWrong(w => w + 1);
      setFeedback({ type: 'wrong', message: `Wrong! Correct: ${correctOrder}` });
    }
    
    setTimeout(() => {
      if (currentRound + 1 >= totalRounds) {
        finishGame(newScore, newCorrect);
      } else {
        setCurrentRound(r => r + 1);
        generateNewRound();
      }
    }, 1500);
  };
  
  // Finish game
  const finishGame = (finalScore = score, finalCorrect = correct) => {
    setGameState(GAME_STATES.FINISHED);
    const timeSpent = Math.round((Date.now() - startTime.current) / 1000);
    const accuracy = (finalCorrect / totalRounds) * 100;
    onComplete({
      score: finalScore,
      maxScore: totalRounds * 10,
      accuracy: accuracy,
      timeSpent: timeSpent
    });
  };
  
  // Device selection screen
  if (gameState === GAME_STATES.READY) {
    return <DeviceModeSelector onSelect={(mode) => { setDeviceMode(mode); setGameState(GAME_STATES.DEVICE_SELECT); }} />;
  }
  
  // Instructions screen
  if (gameState === GAME_STATES.DEVICE_SELECT) {
    const timeLimit = getTimeLimit();
    
    return (
      <Card className="border-slate-200">
        <CardContent className="pt-6 sm:pt-8">
          <div className="text-center mb-4 sm:mb-6">
            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 bg-[#0B1D51]/10 rounded-full flex items-center justify-center">
              <Gauge className="w-8 h-8 sm:w-10 sm:h-10 text-[#0B1D51]" />
            </div>
            <h2 className="font-heading text-xl sm:text-2xl font-bold text-[#0B1D51] mb-2">
              Rule-Based Prioritising
            </h2>
            <p className="text-sm text-[#64748B]">Level {level} - Dials Test</p>
            <p className="text-xs text-[#FF9F1C] font-medium mt-2">
              Develops skills to improve performance on TRP1/TRP2 Rule Application Tests
            </p>
          </div>
          
          <div className="bg-[#F8FAFC] rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
            <h3 className="font-semibold text-[#0B1D51] mb-3 text-sm sm:text-base">How to Play:</h3>
            <ol className="space-y-2 text-xs sm:text-sm text-[#64748B]">
              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">1.</span>
                Three gauges show <strong>Brake Pressure</strong>, <strong>Battery Voltage</strong>, and <strong>Fuel</strong>
              </li>
              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">2.</span>
                System importance order: <strong>Brake &gt; Battery &gt; Fuel</strong>
              </li>
              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">3.</span>
                <span>Zone priority: <span className="text-red-500 font-semibold">Red (High)</span> &gt; <span className="text-orange-500 font-semibold">Orange (Medium)</span> &gt; <span className="text-slate-400 font-semibold">White (Low)</span></span>
              </li>
              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">4.</span>
                Determine the correct <strong>viewing order</strong> (L, M, R) based on priority rules
              </li>
              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">5.</span>
                Red zones come first, then Orange, then White. Within same zone, follow system importance
              </li>
            </ol>
          </div>
          
          <PriorityLegend />
          
          <div className="bg-slate-100 rounded-lg p-3 mb-4 sm:mb-6">
            <p className="text-xs sm:text-sm text-[#64748B]">
              <strong>Example:</strong> If all gauges are in the White zone, the order is <strong>L M R</strong> (by system importance).
              If Battery is in Red and others in White, Battery (M) comes first: <strong>M L R</strong>.
            </p>
          </div>
          
          {timeLimit && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 sm:mb-6">
              <p className="text-xs sm:text-sm text-amber-800 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <strong>Time Limit:</strong> {timeLimit} seconds per question
              </p>
            </div>
          )}
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 sm:mb-6">
            <p className="text-xs sm:text-sm text-blue-800">
              <strong>RIS-3751-TOM:</strong> This exercise develops skills assessed by TRP1 and TRP2 - testing the ability to interpret multiple sources of information, apply rules accurately, and make correct decisions under time constraints.
            </p>
          </div>
          
          <Button 
            onClick={startGame}
            size="lg"
            className="w-full bg-[#0B1D51] hover:bg-[#0B1D51]/90"
            data-testid="start-game-btn"
          >
            <Play className="w-5 h-5 mr-2" />
            Start Test ({totalRounds} questions)
          </Button>
        </CardContent>
      </Card>
    );
  }
  
  // Playing state
  if (gameState === GAME_STATES.PLAYING && dialSet) {
    return (
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="text-center">
            <p className="text-xs text-[#64748B] uppercase tracking-wide">Score</p>
            <p className="font-mono text-xl sm:text-2xl font-bold text-[#0B1D51]">{score}</p>
          </div>
          <Badge variant="outline" className="text-[#64748B] text-xs sm:text-sm">
            Question {currentRound + 1}/{totalRounds}
          </Badge>
          <div className="text-center">
            <p className="text-xs text-[#64748B] uppercase tracking-wide">Correct</p>
            <p className="font-mono text-xl sm:text-2xl font-bold text-[#2CB67D]">{correct}</p>
          </div>
        </div>
        
        {/* Timer */}
        <GameTimer timeLeft={timeLeft} maxTime={getTimeLimit()} />
        
        {/* Priority Legend */}
        <PriorityLegend />
        
        {/* Instructions reminder */}
        <div className="bg-white border border-slate-200 rounded-lg p-2 sm:p-3 text-center">
          <p className="text-xs sm:text-sm text-[#64748B]">
            Select the correct <strong>viewing order</strong> based on priority (Red &gt; Orange &gt; White) and system importance (Brake &gt; Battery &gt; Fuel)
          </p>
        </div>
        
        {/* Dials Display */}
        <Card className="border-slate-200">
          <CardContent className="py-4 sm:py-6">
            <div className="flex justify-center items-end gap-2 sm:gap-6">
              <Dial {...dialSet.L} />
              <Dial {...dialSet.M} />
              <Dial {...dialSet.R} />
            </div>
          </CardContent>
        </Card>
        
        {/* Answer Options */}
        <div className="grid grid-cols-2 gap-3">
          {options.map((option, idx) => (
            <Button
              key={idx}
              variant="outline"
              className={`h-14 sm:h-16 text-lg sm:text-xl font-mono font-bold transition-all ${
                selectedAnswer === option
                  ? option === correctOrder
                    ? 'border-[#2CB67D] bg-[#2CB67D]/20 text-[#2CB67D]'
                    : 'border-red-500 bg-red-500/20 text-red-500'
                  : feedback && option === correctOrder
                    ? 'border-[#2CB67D] bg-[#2CB67D]/20 text-[#2CB67D]'
                    : 'border-slate-200 hover:border-[#0B1D51]'
              }`}
              onClick={() => handleAnswer(option)}
              disabled={!!feedback}
              data-testid={`answer-${option}`}
            >
              {option.split('').join(' ')}
            </Button>
          ))}
        </div>
        
        {/* Feedback */}
        <AnimatePresence>
          {feedback && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className={`p-3 sm:p-4 rounded-lg text-center font-medium text-sm sm:text-base flex items-center justify-center gap-2 ${
                feedback.type === 'correct' 
                  ? 'bg-[#2CB67D] text-white' 
                  : 'bg-red-500 text-white'
              }`}
            >
              {feedback.type === 'correct' ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <XCircle className="w-5 h-5" />
              )}
              {feedback.message}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }
  
  return null;
};

export default DialsGame;
